import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { sessionCache } from '../lib/sessionCache';

interface Profile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  bio: string | null;
  facebook_url: string | null;
  twitter_url: string | null;
  instagram_url: string | null;
  linkedin_url: string | null;
  youtube_url: string | null;
  github_url: string | null;
  created_at: string;
  updated_at: string;
  scheduled_for_deletion_at: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;

      // "Self-healing" logic: if DB profile is missing or has no name, use metadata
      // Supabase stores firstName/lastName in user_metadata if we passed it during signUp
      const metadata = user?.user_metadata || {};
      const fName = data?.first_name || metadata?.first_name || '';
      const lName = data?.last_name || metadata?.last_name || '';

      if (data) {
        const profileData = { 
          ...data, 
          first_name: fName, 
          last_name: lName,
          scheduled_for_deletion_at: data.scheduled_for_deletion_at || null
        };
        setProfile(profileData);
        sessionCache.set(`profile_${userId}`, profileData);
        
        // If the DB was missing names but the metadata had them, update the DB row
        if (!data.first_name && fName && session) {
           await supabase.from('profiles').update({ first_name: fName, last_name: lName }).eq('id', userId);
        }
      } else if (fName || lName) {
        // Create a temporary profile object if the row doesn't exist yet but we have metadata
        const tempProfile = {
          id: userId,
          email: user?.email || '',
          first_name: fName,
          last_name: lName,
          phone: null,
          avatar_url: null,
          bio: null,
          facebook_url: null,
          twitter_url: null,
          instagram_url: null,
          linkedin_url: null,
          youtube_url: null,
          github_url: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          scheduled_for_deletion_at: null
        };
        setProfile(tempProfile);
        sessionCache.set(`profile_${userId}`, tempProfile);
      }
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      // Handle expired JWT by signing out
      if (error?.code === 'PGRST303' || error?.message?.includes('JWT expired')) {
        console.warn('JWT expired dectected in fetchProfile, signing out...');
        setUser(null);
        setSession(null);
        setProfile(null);
        supabase.auth.signOut().catch(() => {});
      }
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    let resolved = false;

    const resolve = () => {
      resolved = true;
      setLoading(false);
    };

    const safeSignOut = async () => {
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
    };

    // Fallback timeout to prevent the app from getting stuck loading
    const timeout = setTimeout(() => {
      if (!resolved) {
        setSession(null);
        setUser(null);
        setProfile(null);
        resolve();
      }
    }, 3000);

    // Handle initial session with error handling
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        console.warn('Error getting session:', error);
        if (error.message?.includes('refresh_token') || error.message?.includes('Invalid Refresh Token')) {
          await safeSignOut();
        }
        setSession(null);
        setUser(null);
        setProfile(null);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          const cachedProfile = sessionCache.get<Profile>(`profile_${session.user.id}`);
          if (cachedProfile) setProfile(cachedProfile);
          fetchProfile(session.user.id);
        } else {
          setProfile(null);
        }
      }
      clearTimeout(timeout);
      resolve();
    }).catch(async () => {
      setSession(null);
      setUser(null);
      setProfile(null);
      clearTimeout(timeout);
      resolve();
    });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // TOKEN_REFRESHED fires every time Supabase silently refreshes the JWT,
      // including on every tab-focus event. Replacing the user/session state
      // object reference here would cascade re-renders through WorkspaceContext
      // and every page, causing a full visible reload on tab switch.
      // Instead, silently swap the session token only when the user hasn't changed.
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        setSession(prev => {
          if (!session) return prev;
          // Only replace if the token actually changed to avoid spurious renders
          return prev?.access_token === session.access_token ? prev : session;
        });
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        // Remove session tokens from the URL hash after a cross-domain redirect
        // so they are never visible in the browser address bar or referrers.
        if (window.location.hash?.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    if (user) {
      sessionCache.remove(`profile_${user.id}`);
    }
    setUser(null);
    setSession(null);
    setProfile(null);
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.warn('Error during supabase sign out', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
